import { useMemo, useState } from 'react';
import { Icon, tokens } from '@/design';
import { appendAuditRow } from '@/state/auditClient';
import { 
  buildPivotUrl, 
  getPivotDefinition, 
  isPivotAvailable,
  type PivotId, 
  type PivotContext 
} from '@/app/pivots/pivotRegistry';

export interface PivotLinkProps {
  /** Pivot type to render */
  pivotId: PivotId;
  /** Context for URL building and return navigation */
  context?: PivotContext;
  /** Optional custom label - defaults to pivot definition name */
  children?: React.ReactNode;
  /** Additional CSS class name */
  className?: string;
  /** Whether to show unavailability reasons */
  showUnavailableReason?: boolean;
  /** Callback when link is clicked (before navigation) */
  onPivot?: (pivotId: PivotId, url: string) => void;
}

/**
 * PivotLink - Token-only accessible link/button for cross-app pivots.
 * 
 * Produces documented URL patterns, opens in new tabs, includes return context,
 * and emits audit rows. If destination app is absent, still renders documented
 * URL and records destination readiness.
 */
export function PivotLink({
  pivotId,
  context = {},
  children,
  className,
  showUnavailableReason = false,
  onPivot,
}: PivotLinkProps) {
  const [toast, setToast] = useState<string | null>(null);
  
  const definition = useMemo(() => {
    try {
      return getPivotDefinition(pivotId);
    } catch (error) {
      console.error('PivotLink: Unknown pivot ID', pivotId, error);
      return null;
    }
  }, [pivotId]);
  
  const isAvailable = useMemo(() => isPivotAvailable(pivotId), [pivotId]);
  
  const url = useMemo(() => {
    if (!definition) return '#';
    try {
      return buildPivotUrl(pivotId, context);
    } catch (error) {
      console.error('PivotLink: Failed to build URL', pivotId, context, error);
      return '#';
    }
  }, [pivotId, context, definition]);
  
  const handleClick = async (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (!definition) {
      event.preventDefault();
      setToast('Unknown pivot configuration');
      return;
    }
    
    // Always emit audit row, even for unavailable destinations
    await appendAuditRow({
      kind: definition.auditKind,
      payload: {
        pivotId,
        targetApp: definition.targetApp,
        resourceId: context.resourceId,
        available: isAvailable,
        url: url !== '#' ? url : null,
      },
      resourceId: context.resourceId,
    });
    
    // Call onPivot callback if provided
    if (onPivot && url !== '#') {
      onPivot(pivotId, url);
    }
    
    // If destination is unavailable but we still have a URL (for result-note tracking)
    if (!isAvailable && url !== '#') {
      // Let the link open (to document the attempt) but show a toast
      setToast(`Opened ${definition.name} - destination readiness unknown`);
    } else if (isAvailable && url !== '#') {
      setToast(`Opened ${definition.name}`);
    } else {
      // Prevent navigation if URL building failed
      event.preventDefault();
      setToast(`Failed to build URL for ${definition.name}`);
    }
  };
  
  if (!definition) {
    if (process.env.NODE_ENV === 'development') {
      return (
        <span 
          className={className}
          style={{ 
            color: tokens.color.semantic.danger,
            fontStyle: 'italic',
          }}
        >
          [Unknown pivot: {pivotId}]
        </span>
      );
    }
    return null;
  }
  
  const label = children || definition.name;
  const ariaLabel = `${definition.name}${definition.isExternal ? ' (external)' : ''} - opens in new tab`;
  const unavailableReason = !isAvailable 
    ? definition.isExternal 
      ? 'External system availability unknown'
      : 'Not available in current environment'
    : null;
    
  return (
    <>
      <a
        href={url !== '#' ? url : undefined}
        target={url !== '#' ? '_blank' : undefined}
        rel={url !== '#' ? 'noopener noreferrer' : undefined}
        aria-disabled={url === '#'}
        aria-label={ariaLabel}
        className={className}
        onClick={handleClick}
        style={{
          minHeight: tokens.density.touchMin,
          display: 'inline-flex',
          alignItems: 'center',
          gap: tokens.space[2],
          border: tokens.border.thin,
          borderRadius: tokens.radius.input,
          background: isAvailable ? tokens.color.accent.primary : tokens.color.surface[2],
          color: isAvailable ? tokens.color.accent.on : tokens.color.ink.secondary,
          paddingInline: tokens.space[3],
          textDecoration: 'none',
          cursor: url !== '#' ? 'pointer' : 'not-allowed',
          // Token-only styling - no custom shadows or decorations
        }}
      >
        <Icon 
          name={definition.isExternal ? 'open_in_new' : 'open_in_new'} 
          size={16} 
          decorative 
        />
        {label}
        {!isAvailable && <Icon name="warning" size={16} decorative />}
      </a>
      
      {toast && (
        <p 
          role="status" 
          style={{ 
            margin: `${tokens.space[2]}px 0 0`, 
            color: isAvailable ? tokens.color.semantic.success : tokens.color.semantic.warning,
            fontSize: tokens.font.size.sm,
          }}
        >
          {toast}
        </p>
      )}
      
      {showUnavailableReason && unavailableReason && (
        <p 
          style={{ 
            margin: `${tokens.space[1]}px 0 0`, 
            color: tokens.color.ink.secondary,
            fontSize: tokens.font.size.sm,
            fontStyle: 'italic',
          }}
        >
          {unavailableReason}
        </p>
      )}
    </>
  );
}

/**
 * Helper component for rendering multiple pivot links in a list.
 */
export interface PivotLinkListProps {
  /** Pivot configurations to render */
  pivots: Array<{
    pivotId: PivotId;
    context?: PivotContext;
    label?: string;
  }>;
  /** Whether to show unavailable pivots */
  showUnavailable?: boolean;
  /** Additional CSS class for the container */
  className?: string;
  /** Callback when any link is clicked */
  onPivot?: (pivotId: PivotId, url: string) => void;
}

export function PivotLinkList({
  pivots,
  showUnavailable = true,
  className,
  onPivot,
}: PivotLinkListProps) {
  const visiblePivots = showUnavailable 
    ? pivots 
    : pivots.filter(({ pivotId }) => isPivotAvailable(pivotId));
    
  if (visiblePivots.length === 0) {
    return null;
  }
  
  return (
    <div 
      className={className}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.space[2],
      }}
    >
      {visiblePivots.map(({ pivotId, context, label }) => (
        <PivotLink
          key={pivotId}
          pivotId={pivotId}
          context={context}
          onPivot={onPivot}
        >
          {label}
        </PivotLink>
      ))}
    </div>
  );
}